/**
 * Shopify Predictive Search for Ella.
 * Products + Blog Posts.
 */

(() => {
	'use strict';

	const MAX_PER_TYPE = 3;
	const MIN_QUERY_LENGTH = 3;
	const DEBOUNCE_MS = 250;

	const shopRoot = (
		window.Shopify?.routes?.root || '/'
	).replace(/\/?$/, '/');

	function el(tag, className, text) {
		const node = document.createElement(tag);

		if (className) node.className = className;
		if (text != null) node.textContent = String(text);

		return node;
	}

	function safeUrl(value) {
		try {
			const result = new URL(
				String(value || ''),
				window.location.origin
			);

			return (
				result.origin === window.location.origin &&
				/^https?:$/.test(result.protocol)
			) ? result.href : '#';
		} catch {
			return '#';
		}
	}

	function getImageUrl(resource, isArticle) {
		const candidate = isArticle
			? (resource.image || resource.featured_image)
			: (resource.featured_image || resource.image);

		const url = typeof candidate === 'string'
			? candidate
			: candidate?.url || candidate?.src;

		if (!url) return '';

		try {
			const parsed = new URL(url, window.location.origin);

			if (!/^https?:$/.test(parsed.protocol)) return '';

			if (parsed.hostname.endsWith('shopify.com')) {
				parsed.searchParams.set('width', '320');
			}

			return parsed.href;
		} catch {
			return '';
		}
	}

	function formatPrice(product) {
		const price = product.price;

		if (price === null || price === undefined || price === '') {
			return '';
		}

		if (window.Shopify?.formatMoney && window.money_format) {
			return window.Shopify.formatMoney(
				price,
				window.money_format
			);
		}

		const cents = Number(price);

		if (!Number.isFinite(cents)) return '';

		const currency =
			window.Shopify?.currency?.active ||
			window.Shopify?.currency?.shop_currency ||
			'USD';

		try {
			return new Intl.NumberFormat(
				document.documentElement.lang || 'en',
				{ style: 'currency', currency }
			).format(cents / 100);
		} catch {
			return (cents / 100).toFixed(2);
		}
	}

	class EllaPredictiveSearch {
		constructor(root) {
			this.root = root;

			this.input = root.querySelector(
				'.header-search__input'
			);

			this.results = root.querySelector(
				'.header-search__results-wrapper'
			);

			this.quickWrapper = root.querySelector(
				'.quickSearchResultsWrap'
			);

			this.popular = root.querySelectorAll(
				'.header-search__product'
			);

			this.trending = root.querySelectorAll(
				'.header-search__trending'
			);

			this.controller = null;
			this.timer = null;
			this.requestId = 0;
			this.lastQuery = '';

			if (!this.input || !this.results) return;

			this.results.setAttribute('aria-live', 'polite');

			this.input.addEventListener('focus', () => {
				this.open();
			});

			this.input.addEventListener('input', () => {
				this.queue();
			});

			this.input.addEventListener('keydown', (event) => {
				if (event.key === 'Escape') {
					this.close();
				}
			});

			document.addEventListener('pointerdown', (event) => {
				if (!this.root.contains(event.target)) {
					this.close();
				}
			});
		}

		open() {
			if (this.quickWrapper) {
				this.quickWrapper.style.display = 'block';
			}

			if (this.results.childElementCount) {
				this.results.hidden = false;
			}

			if (
				this.input.value.trim().length >= MIN_QUERY_LENGTH &&
				this.input.value.trim() !== this.lastQuery
			) {
				this.queue();
			}
		}

		close() {
			this.results.hidden = true;

			if (this.quickWrapper) {
				this.quickWrapper.style.display = 'none';
			}
		}

		showPrompts(show) {
			[...this.popular, ...this.trending].forEach((node) => {
				node.style.display = show ? '' : 'none';
			});
		}

		queue() {
			clearTimeout(this.timer);
			this.controller?.abort();

			this.requestId++;

			const query = this.input.value.trim();

			this.lastQuery = '';

			if (query.length < MIN_QUERY_LENGTH) {
				this.results.replaceChildren();
				this.results.hidden = true;
				this.showPrompts(true);

				if (this.quickWrapper) {
					this.quickWrapper.style.display = 'block';
				}

				return;
			}

			this.showPrompts(false);

			this.results.hidden = false;

			if (this.quickWrapper) {
				this.quickWrapper.style.display = 'block';
			}

			this.results.replaceChildren(
				el('div', 'ella-predictive__message', 'Searching…')
			);

			const currentId = this.requestId;

			this.timer = setTimeout(() => {
				this.search(query, currentId);
			}, DEBOUNCE_MS);
		}

		buildUrl(query) {
			const url = new URL(
				`${shopRoot}search/suggest.json`,
				window.location.origin
			);

			url.searchParams.set('q', query);

			url.searchParams.set(
				'resources[type]',
				'product,article'
			);

			url.searchParams.set(
				'resources[limit]',
				String(MAX_PER_TYPE)
			);

			url.searchParams.set(
				'resources[limit_scope]',
				'each'
			);

			url.searchParams.set(
				'resources[options][unavailable_products]',
				'last'
			);

			return url.href;
		}

		allResultsUrl(query) {
			const url = new URL(
				`${shopRoot}search`,
				window.location.origin
			);

			url.searchParams.set('q', query);
			url.searchParams.set('type', 'product,article');
			url.searchParams.set('options[prefix]', 'last');

			return url.href;
		}

		async search(query, currentId) {
			this.controller = new AbortController();

			try {
				const response = await fetch(this.buildUrl(query), {
					signal: this.controller.signal,
					credentials: 'same-origin',
					headers: {
						Accept: 'application/json'
					}
				});

				if (!response.ok) {
					throw new Error(
						`Predictive search HTTP ${response.status}`
					);
				}

				const data = await response.json();

				if (
					currentId !== this.requestId ||
					this.input.value.trim() !== query
				) {
					return;
				}

				const groups = data?.resources?.results || {};

				this.results.replaceChildren(
					this.render(
						query,
						groups.products || [],
						groups.articles || []
					)
				);

				this.results.hidden = false;
				this.lastQuery = query;

			} catch (error) {
				if (error.name === 'AbortError') return;
				if (currentId !== this.requestId) return;

				console.error('[EllaPredictiveSearch]', error);

				this.results.replaceChildren(
					el(
						'div',
						'ella-predictive__message',
						'Search suggestions are unavailable. Press Enter to see all results.'
					)
				);
			}
		}

		card(item, isArticle) {
			const link = el('a', 'ella-predictive__card');

			link.href = safeUrl(item.url);

			const media = el('span', 'ella-predictive__media');
			const imgUrl = getImageUrl(item, isArticle);

			if (imgUrl) {
				const img = document.createElement('img');

				img.src = imgUrl;
				img.alt = item.title || '';
				img.loading = 'lazy';

				media.appendChild(img);
			} else {
				media.classList.add(
					'ella-predictive__media--empty'
				);
			}

			link.appendChild(media);

			if (!isArticle && item.vendor) {
				link.appendChild(
					el(
						'span',
						'ella-predictive__vendor',
						item.vendor
					)
				);
			}

			link.appendChild(
				el(
					'span',
					'ella-predictive__title',
					item.title || 'Untitled'
				)
			);

			if (!isArticle) {
				const price = formatPrice(item);

				if (price) {
					link.appendChild(
						el('span', 'ella-predictive__price', price)
					);
				}
			}

			return link;
		}

		group(label, items, isArticle) {
			if (!items.length) return null;

			const section = el(
				'section',
				`ella-predictive__group${
					isArticle ? ' ella-predictive__group--articles' : ''
				}`
			);

			section.appendChild(
				el('div', 'box-title', label)
			);

			const grid = el(
				'div',
				isArticle
					? 'search_articles ella-predictive__grid'
					: 'search_products ella-predictive__grid'
			);

			items.slice(0, MAX_PER_TYPE).forEach((item) => {
				grid.appendChild(this.card(item, isArticle));
			});

			section.appendChild(grid);

			return section;
		}


		render(query, products, articles) {
			const content = document.createDocumentFragment();

			const wrapper = el(
				'div',
				'header-search__results header-block ella-predictive'
			);

			const productGroup = this.group(
				'PRODUCT RESULTS',
				products,
				false
			);

			const articleGroup = this.group(
				'BLOG RESULTS',
				articles,
				true
			);

			if (productGroup) {
				wrapper.appendChild(productGroup);
			}

			if (articleGroup) {
				wrapper.appendChild(articleGroup);
			}

			const hasResults = Boolean(productGroup || articleGroup);

			if (!hasResults) {
				wrapper.appendChild(
					el(
						'div',
						'ella-predictive__message',
						'No suggestions found.'
					)
				);
			} else {
				const footer = el(
					'div',
					'text-center ella-predictive__footer'
				);

				const seeAll = el(
					'a',
					'ella-predictive__all',
					'SEE ALL RESULTS'
				);

				seeAll.href = this.allResultsUrl(query);

				footer.appendChild(seeAll);
				wrapper.appendChild(footer);
			}

			content.appendChild(wrapper);

			return content;
		}
	}

	function init() {
		document.querySelectorAll('[data-ajax-search]').forEach((root) => {
			if (root.dataset.nativePredictiveInitialized === 'true') {
				return;
			}

			root.dataset.nativePredictiveInitialized = 'true';

			new EllaPredictiveSearch(root);
		});
	}

	if (document.readyState === 'loading') {
		document.addEventListener('DOMContentLoaded', init);
	} else {
		init();
	}
})();
